/**
 * BrightLabsCMS — scoped CMS plugin for BrightLabs Growth Engine pages.
 *
 * Hardened install for the Cobra Thai site:
 *  - Mounted ONLY inside the public marketing site under /services/* (never
 *    /admin, /portal, /api — those are separate route trees gated by path prefix).
 *  - Remote HTML is sanitised with DOMPurify before rendering (defence in depth).
 *  - Reads no localStorage / sessionStorage / cookies.
 *  - Only outbound calls: GET .../pages/<slug> and a fire-and-forget telemetry POST.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';

const CMS_API = process.env.REACT_APP_BL_CMS_API || '';
const SITE_ID = process.env.REACT_APP_BL_SITE_ID || '';

/** Anonymous, fire-and-forget analytics beacon. No cookies / no visitor id. */
function sendTelemetry(path) {
  if (!CMS_API || !SITE_ID) return;
  try {
    const body = {
      path: path || (typeof window !== 'undefined' ? window.location.pathname : '/'),
      referrer: (typeof document !== 'undefined' && document.referrer) || null,
      timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || null,
      ua: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
    };
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) setTimeout(() => controller.abort(), 3000);
    fetch(`${CMS_API}/${SITE_ID}/telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller?.signal,
    }).catch(() => {});
  } catch (_e) {
    // no-op
  }
}

/** Fetch a page by slug. Returns { page, status, error }. */
export function useBrightLabsPage(slug) {
  const [state, setState] = useState({ page: null, status: 'loading', error: null });
  useEffect(() => {
    if (!CMS_API || !SITE_ID) {
      setState({ page: null, status: 'unconfigured', error: 'BrightLabs CMS env vars missing' });
      return;
    }
    let cancelled = false;
    const s = slug.replace(/^\//, '');
    setState({ page: null, status: 'loading', error: null });
    fetch(`${CMS_API}/${SITE_ID}/pages/${encodeURIComponent(s)}`)
      .then((r) => {
        if (r.ok) return r.json();
        return Promise.reject({ code: r.status, message: `HTTP ${r.status}` });
      })
      .then((page) => { if (!cancelled) setState({ page, status: 'ready', error: null }); })
      .catch((err) => {
        if (cancelled) return;
        const isTypeError = err instanceof TypeError;
        const code = err && err.code;
        const status = code === 404 ? 'not_found' : (isTypeError ? 'network' : 'error');
        console.warn('[BrightLabsCMS]', { slug: s, status, error: err });
        setState({ page: null, status, error: err });
      });
    return () => { cancelled = true; };
  }, [slug]);
  return state;
}

/** Renders whatever BrightLabs page matches the current URL under basePath. */
export function BrightLabsCmsPage({ renderPage, basePath }) {
  const location = useLocation();
  let path = location.pathname;
  if (basePath) {
    const bp = basePath.startsWith('/') ? basePath : '/' + basePath;
    if (path.startsWith(bp)) path = path.slice(bp.length) || '/';
  }
  const { page, status } = useBrightLabsPage(path);

  useEffect(() => {
    if (!page) return;
    if (page.meta_title) document.title = page.meta_title;
    if (page.meta_description) {
      let m = document.head.querySelector('meta[name="description"]');
      if (!m) {
        m = document.createElement('meta');
        m.setAttribute('name', 'description');
        document.head.appendChild(m);
      }
      m.setAttribute('content', page.meta_description);
    }
    sendTelemetry(location.pathname);
  }, [page?.id]);

  if (status === 'loading') {
    return <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>Loading…</div>;
  }
  if (status === 'not_found') {
    return (
      <div style={{ padding: 80, textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 40, marginBottom: 12, color: '#0f172a' }}>Page not found</h1>
        <p style={{ color: '#64748b' }}>The page you&rsquo;re looking for doesn&rsquo;t exist. <a href="/">Go home</a>.</p>
      </div>
    );
  }
  if (status === 'network') {
    return (
      <div style={{ padding: 80, textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 40, marginBottom: 12, color: '#0f172a' }}>Content unavailable</h1>
        <p style={{ color: '#64748b' }}>We couldn&rsquo;t load this page right now. Please try again shortly.</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ padding: 80, textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 40, marginBottom: 12, color: '#0f172a' }}>Something went wrong</h1>
        <p style={{ color: '#64748b' }}>This page failed to load. Try refreshing.</p>
      </div>
    );
  }
  if (status === 'unconfigured') {
    return null;
  }
  if (!page) return null;
  if (renderPage) return renderPage(page);
  return <DefaultPageLayout page={page} />;
}

/** Minimal page layout. Remote HTML is sanitised with DOMPurify before render. */
function DefaultPageLayout({ page }) {
  const cleanHtml = DOMPurify.sanitize(page.content || '', { USE_PROFILES: { html: true } });
  return (
    <article style={{ maxWidth: 780, margin: '0 auto', padding: '48px 20px' }}>
      <h1 style={{ fontSize: 44, lineHeight: 1.1, marginBottom: 20, color: '#0f172a' }}>{page.title}</h1>
      {page.meta_description && (
        <p style={{ fontSize: 18, color: '#475569', marginBottom: 32 }}>{page.meta_description}</p>
      )}
      <div
        style={{ fontSize: 17, lineHeight: 1.7, color: '#1e293b' }}
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    </article>
  );
}

export default BrightLabsCmsPage;
